import type {LlmProxyClient} from '@wandb/weave/lib/LlmProxy/LlmProxyClient';

import {assembleSystemPrompt, buildContextSegment} from '../prompt/assemble';
import {
  BEHAVIORAL_PROMPT,
  IDENTITY_PROMPT,
  WORKFLOW_HINTS,
} from '../prompt/static';
import {extractErrorMessage} from './errors';
import type {RuntimeEvent} from './events';
import type {
  PendingToolCall,
  ProviderAdapter,
  RuntimeConfig,
} from './providers/adapter';
import type {AgentRuntime, GetThreadMessages, RunTurnParams} from './runtime';

const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  model: 'gpt-4.1',
  maxIterations: 10,
};

/**
 * ClientAgentRuntime drives a single LLM call from the browser: stream SSE
 * from the proxy and decode chunks into RuntimeEvents.
 *
 * Tool calls are streamed to the UI but not executed client-side.
 * `finalizeInProgressParts` ensures orphaned tool calls display correctly
 * when the run ends.
 *
 * TODO: Replace with a backend runtime that owns the loop server-side.
 * This class, ProviderAdapter, and all provider implementations are removed
 * once that migration is complete. The AgentRuntime interface and
 * RuntimeEvent protocol remain.
 */
export class ClientAgentRuntime implements AgentRuntime {
  constructor(
    private readonly client: LlmProxyClient,
    private readonly adapter: ProviderAdapter,
    private readonly getThreadMessages: GetThreadMessages
  ) {}

  async *runTurn({
    threadId,
    userMessage,
    parentId,
    context,
    config: configOverride,
    tools: toolsOrGetter,
    signal,
  }: RunTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const config = configOverride ?? DEFAULT_RUNTIME_CONFIG;
    const runId = crypto.randomUUID();

    try {
      yield {type: 'run-started', runId, threadId};

      const history = this.getThreadMessages(threadId, parentId ?? null);
      if (userMessage !== undefined) {
        history.push({
          role: 'user',
          content: [{type: 'text', text: userMessage}],
        });
      }

      const systemPrompt = assembleSystemPrompt({
        identity: IDENTITY_PROMPT,
        behavioral:
          BEHAVIORAL_PROMPT + (WORKFLOW_HINTS ? '\n\n' + WORKFLOW_HINTS : ''),
        context: buildContextSegment(context ?? {}),
      });

      const conversation = this.adapter.toProviderMessages(
        history,
        systemPrompt
      );

      const messageId = crypto.randomUUID();
      const tools =
        typeof toolsOrGetter === 'function'
          ? toolsOrGetter()
          : (toolsOrGetter ?? []);
      const toolCalls: PendingToolCall[] = [];
      const decodeChunk = this.adapter.createChunkDecoder(messageId);
      const body = this.adapter.buildRequestBody(conversation, tools, config);
      const stream = this.client.proxyStream(
        this.adapter.targetHost,
        this.adapter.targetPath,
        body,
        undefined,
        undefined,
        {signal}
      );

      for await (const chunk of stream) {
        if (signal.aborted) {
          break;
        }
        for (const event of decodeChunk(chunk, toolCalls)) {
          yield event;
        }
      }

      yield {type: 'run-finished', runId};
    } catch (err) {
      if (signal.aborted) {
        yield {type: 'run-finished', runId};
      } else {
        yield {
          type: 'run-error',
          runId,
          message: extractErrorMessage(err),
        };
      }
    }
  }
}
